import type {
  BannerData,
  MarketingRankingItem,
  RawMarketingRuntimeConfigData,
  MarketingRuntimeConfig,
  MarketingSceneProductsBlockConfig,
  MarketingSceneProductsItem,
} from '../[slug]/types';
import type { IProductListApiParams, ProductRankingTagType } from '@/api/types/product';
import type {
  BestsellerInStockFilter,
  BestsellerPageData,
  BestsellerPriceInfo,
  BestsellerProduct,
  BestsellerRuntimeData,
  BestsellerRuntimeRanking,
  BestsellerVibe,
} from './types';
import { VIBE_LOAD_BATCH_SIZE } from './vibeCarousel';

type RuntimeProduct = Record<string, unknown>;

export const EMPTY_BESTSELLER_PAGE_DATA: BestsellerPageData = {
  hero: {
    title: 'Best Sellers',
    subtitle: '',
  },
  tabs: [],
  rankingNote: '',
  rankings: {},
  vibeHeading: '',
  vibeSubtitle: '',
  vibes: [],
  vibeProducts: [],
};

interface BuildBestsellerPageDataParams {
  runtimeConfig: MarketingRuntimeConfig;
  rankingProductsByIndex: Map<number, RuntimeProduct[]>;
  rankingProductTotalsByIndex?: Map<number, number>;
  rankingProductHasMoreByIndex?: Map<number, boolean>;
  loadedRankingIndexes?: Set<number>;
  sceneProductsByIndex: Map<number, RuntimeProduct[]>;
  sceneProductTotalsByIndex?: Map<number, number>;
  sceneProductHasMoreByIndex?: Map<number, boolean>;
  fallback: BestsellerPageData;
  s3BaseUrl?: string;
  bestsellerRuntimeData?: BestsellerRuntimeData;
  isWap?: boolean;
  isApp?: boolean;
}

function stringValue(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return typeof value === 'string' ? value.trim() : '';
}

function firstString(...values: unknown[]): string {
  for (const value of values) {
    const result = stringValue(value);
    if (result) return result;
  }
  return '';
}

function recordValue(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function numberValue(value: unknown): number {
  const result = Number(value);
  return Number.isFinite(result) ? result : 0;
}

function optionalNumberValue(value: unknown): number | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  const result = Number(value);
  return Number.isFinite(result) ? result : undefined;
}

function sortedRecords(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item): item is Record<string, unknown> => Boolean(item && typeof item === 'object'))
    .sort((a, b) => numberValue(a.sort) - numberValue(b.sort));
}

function firstNonEmptyArray(...values: unknown[]): unknown[] {
  return (values.find((value) => Array.isArray(value) && value.length > 0) as unknown[]) || [];
}

function firstRecord(...values: unknown[]): Record<string, unknown> {
  for (const value of values) {
    const record = recordValue(value);
    if (Object.keys(record).length > 0) return record;
  }
  return {};
}

function rankingProductLabel(value: unknown): BestsellerProduct['tagType'] {
  const label = firstString(value);
  if (label === 'sales' || label === 'trend') return label;
  return undefined;
}

function runtimeProductGroupId(item: Record<string, unknown>, fallback = 0): number {
  const productTemplate = recordValue(item.product_template);
  return (
    numberValue(item.product_group_id) || numberValue(productTemplate.product_group_id) || fallback
  );
}

function sceneProductGroupId(sceneBlock: Record<string, unknown>): number {
  return runtimeProductGroupId(sceneBlock);
}

function stockSortFirstValue(value: unknown): number | undefined {
  const result = optionalNumberValue(value);
  return result === 1 || result === 2 ? result : undefined;
}

function stockSortFirstFromSoldOutSort(value: unknown): number | undefined {
  const soldOutSort = optionalNumberValue(value);
  if (soldOutSort === 1) return 2;
  if (soldOutSort === 2) return 1;
  return undefined;
}

function sceneStockSortFirst(
  item: Record<string, unknown>,
  fallbackStockSortFirst?: number,
): number | undefined {
  return (
    stockSortFirstValue(item.stock_sort_first) ??
    stockSortFirstFromSoldOutSort(item.sold_out_sort) ??
    fallbackStockSortFirst
  );
}

function sceneInStockFilter(
  item: Record<string, unknown>,
  fallbackInStockFilter: BestsellerInStockFilter = 3,
): BestsellerInStockFilter {
  const configuredInStock = optionalNumberValue(item.in_stock);
  if (configuredInStock === 1 || configuredInStock === 2 || configuredInStock === 3) {
    return configuredInStock;
  }

  const soldOutDisplay = optionalNumberValue(item.sold_out_display);
  if (soldOutDisplay === 1) return 3;
  if (soldOutDisplay === 2) return 1;
  return fallbackInStockFilter;
}

function normalizeRuntimeScene(
  item: Record<string, unknown>,
  index: number,
  fallbackProductGroupId: number,
  fallbackStockSortFirst?: number,
  fallbackInStockFilter: BestsellerInStockFilter = 3,
): BestsellerRuntimeData['scenes'][number] {
  const title = firstString(
    item.title,
    item.scene_title,
    item.style_title,
    item.nav_title,
    item.name,
  );
  const resolvedStockSortFirst = sceneStockSortFirst(item, fallbackStockSortFirst);
  const inStockFilter = sceneInStockFilter(item, fallbackInStockFilter);

  return {
    id: firstString(item.uid) || sceneId(title, index),
    title: title || `Vibe ${index + 1}`,
    productGroupId: runtimeProductGroupId(item, fallbackProductGroupId),
    categoryKey: firstString(
      item.category_key,
      item.categoryKey,
      item.attribute_key,
      item.attr_key,
    ),
    attributeValue: firstString(
      item.attribute_value,
      item.attributeValue,
      item.attr_value,
      item.option_value,
      item.value,
    ),
    ...(resolvedStockSortFirst ? { stockSortFirst: resolvedStockSortFirst } : {}),
    inStockFilter,
  };
}

export function normalizeBestsellerRuntimeScenesFromMarketingBlock(
  sceneBlock?: MarketingSceneProductsBlockConfig,
): BestsellerRuntimeData['scenes'] {
  if (!sceneBlock || sceneBlock.visible === false) return [];

  const sceneGroupId = sceneProductGroupId(sceneBlock as unknown as Record<string, unknown>);
  const fallbackStockSortFirst = sceneStockSortFirst(
    sceneBlock as unknown as Record<string, unknown>,
  );
  const fallbackInStockFilter = sceneInStockFilter(
    sceneBlock as unknown as Record<string, unknown>,
  );
  return sortedRecords(sceneBlock.scenes)
    .filter((item) => item.visible !== false && item.enabled !== false)
    .map((item, index) =>
      normalizeRuntimeScene(
        item,
        index,
        sceneGroupId,
        fallbackStockSortFirst,
        fallbackInStockFilter,
      ),
    );
}

export function normalizeBestsellerRuntimeData(
  rawConfig: RawMarketingRuntimeConfigData,
): BestsellerRuntimeData {
  const blocks = recordValue(rawConfig.blocks);
  const marketingData = recordValue(blocks.marketing_data);
  const banner = recordValue(blocks.ad_banner);
  const rankingBlock = firstRecord(blocks.ranking_block, marketingData.ranking_block);
  const sceneBlock = firstRecord(blocks.scene_products, marketingData.scene_products);
  const seo = recordValue(rawConfig.seo);
  const sceneGroupId = sceneProductGroupId(sceneBlock);
  const fallbackStockSortFirst = sceneStockSortFirst(sceneBlock);
  const fallbackInStockFilter = sceneInStockFilter(sceneBlock);
  const sceneRecords = sortedRecords(
    firstNonEmptyArray(
      sceneBlock.scenes,
      sceneBlock.scene_list,
      sceneBlock.scene_product_list,
      sceneBlock.category_list,
      sceneBlock.style_list,
      sceneBlock.my_product_list,
    ),
  ).filter((item) => item.visible !== false && item.enabled !== false);

  const configuredRankingRecords = firstNonEmptyArray(
    rankingBlock.rankings,
    rankingBlock.ranking_list,
    rankingBlock.rank_list,
    rankingBlock.list,
    rankingBlock.tabs,
    rankingBlock.my_product_list,
  );
  const rankingBlockProductGroupId = runtimeProductGroupId(rankingBlock);
  const rankingRecords =
    configuredRankingRecords.length > 0
      ? configuredRankingRecords
      : rankingBlockProductGroupId
        ? [rankingBlock]
        : [];
  const rankings: BestsellerRuntimeRanking[] = sortedRecords(rankingRecords)
    .filter((item) => item.visible !== false && item.enabled !== false)
    .slice(0, 3)
    .map((item, index) => {
      const productLabel = rankingProductLabel(item.product_label);

      return {
        id:
          firstString(item.uid) ||
          tabId(firstString(item.tab_title, item.tab_name, item.title, item.name), index),
        title:
          firstString(
            item.tab_title,
            item.tab_name,
            item.ranking_title,
            item.rank_title,
            item.title,
            item.name,
          ) || `Ranking ${index + 1}`,
        subtitle: firstString(
          item.tab_description,
          item.tab_desc,
          item.sub_title,
          item.subtitle,
          item.description,
          rankingBlock.sub_title,
        ),
        icon: firstString(item.tab_icon) || undefined,
        productLabel,
        showProductLabel: index < 2 && Boolean(productLabel),
        productGroupId: runtimeProductGroupId(item, rankingBlockProductGroupId),
        topProducts: sortedRecords(item.top_products).map((topProduct, topIndex) => ({
          rank: numberValue(topProduct.rank) || topIndex + 1,
          pcInfluencerImage: firstString(topProduct.pc_influencer_image) || undefined,
          wapInfluencerImage: firstString(topProduct.wap_influencer_image) || undefined,
        })),
      };
    });

  return {
    hero: {
      title: firstString(seo.seo_h1, seo.h_one, rawConfig.page_title),
      subtitle: firstString(banner.sub_title),
      alt: firstString(banner.alt) || undefined,
      pcTitleImage: firstString(banner.pc_title_image) || undefined,
      wapTitleImage: firstString(banner.wap_title_image) || undefined,
      trackingJumpContent:
        banner.jump && typeof banner.jump === 'object'
          ? (banner.jump as BannerData['jump_content'])
          : undefined,
    },
    defaultTabIds: {
      pc: firstString(rankingBlock.pc_default_tab_uid),
      wap: firstString(rankingBlock.wap_default_tab_uid),
      app: firstString(rankingBlock.app_default_tab_uid),
    },
    rankingBackgroundColor: firstString(rankingBlock.background_color) || undefined,
    rankingNote: firstString(rankingBlock.sub_title),
    rankings,
    vibeHeading: firstString(sceneBlock.title),
    vibeSubtitle: firstString(sceneBlock.sub_title),
    scenes: sceneRecords.map((item, index) =>
      normalizeRuntimeScene(
        item,
        index,
        sceneGroupId,
        fallbackStockSortFirst,
        fallbackInStockFilter,
      ),
    ),
  };
}

export function buildBestsellerProductListParams(
  productGroupId: number,
  sceneFilter?: { categoryKey?: string; attributeValue?: string },
  limit = 20,
  stockSortFirst?: number,
  inStockFilter: BestsellerInStockFilter = 3,
): IProductListApiParams {
  const configuredCategoryKey = sceneFilter?.categoryKey;
  const categoryKey =
    configuredCategoryKey === 'product_color' ? 'product_frame_color' : configuredCategoryKey;
  const attributeValue = sceneFilter?.attributeValue;
  const filter =
    categoryKey && attributeValue
      ? ({ [categoryKey]: [attributeValue] } as IProductListApiParams['filter'])
      : undefined;

  return {
    group_id: productGroupId,
    limit,
    page: 1,
    is_sale: 1,
    in_stock: inStockFilter,
    append_correlation: 1,
    show_adv: 2,
    ...(filter ? { filter } : {}),
    ...(stockSortFirst ? { stock_sort_first: stockSortFirst } : {}),
  };
}

export function resolveProductRankingTagType(
  tagType: BestsellerProduct['tagType'],
): ProductRankingTagType {
  return tagType === 'trend' ? 2 : 1;
}

function withAssetBase(path: string, s3BaseUrl: string): string {
  if (!path || /^(https?:)?\/\//.test(path) || path.startsWith('/images/')) return path;
  if (!s3BaseUrl) return path;
  return `${s3BaseUrl.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
}

function imagePaths(product: RuntimeProduct, group: string, s3BaseUrl: string): string[] {
  const image = product.image;
  if (!image || typeof image !== 'object') return [];
  const values = (image as Record<string, unknown>)[group];
  if (!Array.isArray(values)) return [];

  return values
    .map((value) => {
      if (typeof value === 'string') return value;
      if (!value || typeof value !== 'object') return '';
      const record = value as Record<string, unknown>;
      return firstString(record.url, record.path, record.src, record.image_url);
    })
    .filter(Boolean)
    .map((path) => withAssetBase(path, s3BaseUrl));
}

function displayPrice(value: string, currencySymbol: string): string {
  if (!value) return '';
  return /^[^\d.-]/.test(value) ? value : `${currencySymbol}${value}`;
}

function priceValues(product: RuntimeProduct): { price: string; originPrice: string } {
  const priceInfoCandidates = [
    product.price_info_v3,
    product.price_info_v2,
    product.price_info_v1,
    product.price_info_v0,
    product.price_info,
  ];
  const priceInfo = priceInfoCandidates.find(Array.isArray) as
    | Array<Record<string, unknown>>
    | undefined;
  const storePrice = priceInfo?.[0] || {};
  const price = firstString(
    product.sell_price,
    product.sale_price,
    storePrice.sell_price,
    storePrice.price,
    product.price,
  );
  const originPrice = firstString(
    product.original_price,
    product.market_price,
    storePrice.price,
    product.price,
    price,
  );

  return { price, originPrice };
}

function trackingPriceInfo(value: unknown): BestsellerPriceInfo[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((item) => {
      const price = recordValue(item);
      return {
        currency: firstString(price.currency),
        price: firstString(price.price),
        sell_price: firstString(price.sell_price),
      };
    })
    .filter((item) => item.currency);
}

function soldLabel(product: RuntimeProduct): string {
  const rankingTag = recordValue(product.ranking_tag);
  const rankingTagValue = optionalNumberValue(rankingTag.value);
  const hasRankingTagText = Object.prototype.hasOwnProperty.call(rankingTag, 'text');
  if (rankingTagValue !== undefined || hasRankingTagText) {
    if (typeof rankingTag.text === 'string') return rankingTag.text;
    if (typeof rankingTag.text === 'number' && Number.isFinite(rankingTag.text)) {
      return String(rankingTag.text);
    }
    return '';
  }

  return firstString(product.sold_text, product.sales_text, product.rank_text, product.hot_text);
}

function productHref(product: RuntimeProduct, shopSku: string): string {
  return (
    firstString(
      product.url,
      product.href,
      product.product_url,
      product.jump_url,
      product.seo_url,
    ) || (shopSku ? `/goods-detail/${shopSku}` : '')
  );
}

export function mapRuntimeBestsellerProduct(
  product: RuntimeProduct,
  rank: number,
  s3BaseUrl = '',
  tagType: BestsellerProduct['tagType'] = 'sales',
  showProductLabel = true,
): BestsellerProduct {
  const shopSku = firstString(product.shop_sku, product.business_sku, product.sku);
  const businessSku = firstString(product.business_sku, product.shop_sku, product.sku);
  const productSku = firstString(product.sku, product.business_sku, product.shop_sku);
  const name = firstString(product.short_name, product.name, product.product_name, shopSku);
  const productImages = imagePaths(product, 'product', s3BaseUrl);
  const productGrayImages = imagePaths(product, 'product_gray', s3BaseUrl);
  const materialImages = imagePaths(product, 'material', s3BaseUrl);
  const celebrityImages = imagePaths(product, 'celebrity', s3BaseUrl);
  const productImage =
    productImages[0] ||
    withAssetBase(
      firstString(product.img_url, product.main_image, product.product_image),
      s3BaseUrl,
    );
  const modelImage = materialImages[0] || celebrityImages[0];
  const { price, originPrice } = priceValues(product);
  const currencySymbol = firstString(product.currency_symbol, product.currency_sign) || '$';
  const id = Number(product.id);
  const productLabel = showProductLabel ? soldLabel(product) : '';

  return {
    id: Number.isFinite(id) ? id : rank,
    rank,
    name,
    shortName: name,
    shopSku,
    businessSku,
    productSku,
    productClassName: firstString(product.products_category),
    description: firstString(product.description, product.product_description),
    price: displayPrice(price, currencySymbol),
    priceValue: price,
    originPrice: displayPrice(originPrice, currencySymbol),
    originPriceValue: originPrice,
    rating: firstString(product.spu_review_stars, product.rating, product.review_stars),
    purchased: productLabel,
    status: optionalNumberValue(product.status),
    stock: optionalNumberValue(product.stock),
    inStock: firstString(product.in_stock) || undefined,
    isGlass: optionalNumberValue(product.is_glass),
    isWish: optionalNumberValue(product.is_wish),
    wishNum: optionalNumberValue(product.wish_num),
    priceInfo: trackingPriceInfo(product.price_info),
    priceInfoV0: trackingPriceInfo(product.price_info_v0),
    priceInfoV1: trackingPriceInfo(product.price_info_v1),
    priceInfoV2: trackingPriceInfo(product.price_info_v2),
    priceInfoV3: trackingPriceInfo(product.price_info_v3),
    priceInfoPc: trackingPriceInfo(product.price_info_pc),
    priceInfoPcV0: trackingPriceInfo(product.price_info_pc_v0),
    priceInfoPcV1: trackingPriceInfo(product.price_info_pc_v1),
    priceInfoPcV2: trackingPriceInfo(product.price_info_pc_v2),
    priceInfoPcV3: trackingPriceInfo(product.price_info_pc_v3),
    priceInfoWap: trackingPriceInfo(product.price_info_wap),
    priceInfoWapV0: trackingPriceInfo(product.price_info_wap_v0),
    priceInfoWapV1: trackingPriceInfo(product.price_info_wap_v1),
    priceInfoWapV2: trackingPriceInfo(product.price_info_wap_v2),
    priceInfoWapV3: trackingPriceInfo(product.price_info_wap_v3),
    discountPercentV0: optionalNumberValue(product.discount_percent_v0),
    discountPercentV1: optionalNumberValue(product.discount_percent_v1),
    discountPercentV2: optionalNumberValue(product.discount_percent_v2),
    discountPercentV3: optionalNumberValue(product.discount_percent_v3),
    productImage,
    productImages,
    productGrayImages,
    materialImages,
    celebrityImages,
    modelImage,
    mobileModelImage: withAssetBase(firstString(product.wap_model_image), s3BaseUrl) || undefined,
    href: productHref(product, shopSku),
    colors: Array.isArray(product.colors) ? product.colors.map(stringValue).filter(Boolean) : [],
    correlation: Array.isArray(product.correlation)
      ? product.correlation.filter((item): item is Record<string, unknown> =>
          Boolean(item && typeof item === 'object'),
        )
      : [],
    tagType: showProductLabel ? tagType : undefined,
  };
}

function tabId(title: string, index: number): string {
  const normalized = title.toLowerCase();
  if (normalized.includes('classic')) return 'classic';
  if (normalized.includes('trend')) return 'trending';
  return normalized.replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || `ranking-${index + 1}`;
}

function sceneId(title: string, index: number): string {
  const normalized = title.toLowerCase();
  if (normalized === 'all') return 'all';
  return normalized.replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || `vibe-${index + 1}`;
}

function moduleProducts(
  item: MarketingRankingItem | MarketingSceneProductsItem,
  productsByIndex: Map<number, RuntimeProduct[]>,
): RuntimeProduct[] {
  const index = item.item_index ?? 0;
  const runtimeProducts = productsByIndex.get(index);
  if (runtimeProducts?.length) return runtimeProducts;
  if (item.products.length) return item.products;
  return item.product_template?.products || [];
}

export function buildBestsellerPageData({
  runtimeConfig,
  rankingProductsByIndex,
  rankingProductTotalsByIndex = new Map(),
  rankingProductHasMoreByIndex = new Map(),
  loadedRankingIndexes,
  sceneProductsByIndex,
  sceneProductTotalsByIndex = new Map(),
  sceneProductHasMoreByIndex = new Map(),
  fallback,
  s3BaseUrl = '',
  bestsellerRuntimeData,
  isWap = false,
  isApp = false,
}: BuildBestsellerPageDataParams): BestsellerPageData {
  if (bestsellerRuntimeData?.rankings.length || bestsellerRuntimeData?.scenes.length) {
    const resolvedLoadedRankingIndexes =
      loadedRankingIndexes ?? new Set(rankingProductsByIndex.keys());
    const rankings: BestsellerPageData['rankings'] = {};
    const tabs = bestsellerRuntimeData.rankings.flatMap((ranking, index) => {
      const topProductImages = new Map(
        ranking.topProducts.map((product) => [product.rank, product] as const),
      );
      const rankingProducts = rankingProductsByIndex.get(index) || [];
      const configuredTotal = rankingProductTotalsByIndex.get(index);
      const total = Math.max(configuredTotal ?? rankingProducts.length, rankingProducts.length);
      const hasMore = rankingProductHasMoreByIndex.get(index) ?? total > rankingProducts.length;
      if (resolvedLoadedRankingIndexes.has(index) && rankingProducts.length === 0 && !hasMore) {
        return [];
      }

      rankings[ranking.id] = rankingProducts.map((product, productIndex) => {
        const rank = productIndex + 1;
        const mappedProduct = mapRuntimeBestsellerProduct(
          product,
          rank,
          s3BaseUrl,
          ranking.productLabel,
          ranking.showProductLabel,
        );
        const topProduct = topProductImages.get(rank);
        return {
          ...mappedProduct,
          ...(topProduct
            ? {
                modelImage:
                  withAssetBase(topProduct.pcInfluencerImage || '', s3BaseUrl) ||
                  mappedProduct.modelImage,
                mobileModelImage:
                  withAssetBase(topProduct.wapInfluencerImage || '', s3BaseUrl) ||
                  mappedProduct.mobileModelImage,
              }
            : {}),
          labelIcon: ranking.showProductLabel
            ? withAssetBase(ranking.icon || '', s3BaseUrl) || undefined
            : undefined,
        };
      });

      return [
        {
          id: ranking.id,
          label: ranking.title,
          eyebrow: ranking.subtitle,
          icon: withAssetBase(ranking.icon || '', s3BaseUrl) || undefined,
          productLabel: ranking.productLabel,
          showProductLabel: ranking.showProductLabel,
          topProducts: ranking.topProducts.map((product) => ({
            ...product,
            pcInfluencerImage:
              withAssetBase(product.pcInfluencerImage || '', s3BaseUrl) || undefined,
            wapInfluencerImage:
              withAssetBase(product.wapInfluencerImage || '', s3BaseUrl) || undefined,
          })),
          productGroupId: ranking.productGroupId,
          total,
          hasMore,
          initiallyLoaded: resolvedLoadedRankingIndexes.has(index),
        },
      ];
    });
    const vibes: BestsellerVibe[] = bestsellerRuntimeData.scenes.map((scene, index) => {
      const sceneProducts = sceneProductsByIndex.get(index) || [];
      const configuredTotal = sceneProductTotalsByIndex.get(index);
      const total = Math.max(configuredTotal ?? sceneProducts.length, sceneProducts.length);
      const stockSortFirst = scene.stockSortFirst;
      return {
        id: scene.id,
        label: scene.title,
        products: sceneProducts.map((product, productIndex) =>
          mapRuntimeBestsellerProduct(product, productIndex + 1, s3BaseUrl),
        ),
        total,
        productGroupId: scene.productGroupId,
        categoryKey: scene.categoryKey,
        attributeValue: scene.attributeValue,
        ...(stockSortFirst ? { stockSortFirst } : {}),
        inStockFilter: scene.inStockFilter,
        hasMore:
          sceneProductHasMoreByIndex.get(index) ??
          (configuredTotal === undefined
            ? sceneProducts.length >= VIBE_LOAD_BATCH_SIZE
            : total > sceneProducts.length),
      };
    });
    const configuredVibeProducts = vibes.find((vibe) => vibe.products?.length)?.products || [];
    const configuredDefaultTabId = isApp
      ? bestsellerRuntimeData.defaultTabIds.app
      : isWap
        ? bestsellerRuntimeData.defaultTabIds.wap
        : bestsellerRuntimeData.defaultTabIds.pc;

    return {
      hero: {
        title: bestsellerRuntimeData.hero.title || fallback.hero.title,
        subtitle: bestsellerRuntimeData.hero.subtitle,
        alt: bestsellerRuntimeData.hero.alt,
        pcTitleImage:
          withAssetBase(bestsellerRuntimeData.hero.pcTitleImage || '', s3BaseUrl) || undefined,
        wapTitleImage:
          withAssetBase(bestsellerRuntimeData.hero.wapTitleImage || '', s3BaseUrl) || undefined,
        trackingJumpContent: bestsellerRuntimeData.hero.trackingJumpContent,
      },
      tabs,
      defaultRankingTabId: tabs.some((tab) => tab.id === configuredDefaultTabId)
        ? configuredDefaultTabId
        : tabs[0]?.id,
      rankingBackgroundColor: bestsellerRuntimeData.rankingBackgroundColor,
      rankingNote: bestsellerRuntimeData.rankingNote,
      rankings,
      vibeHeading: bestsellerRuntimeData.vibeHeading || fallback.vibeHeading,
      vibeSubtitle: bestsellerRuntimeData.vibeSubtitle || fallback.vibeSubtitle,
      vibes: vibes.length ? vibes : fallback.vibes,
      vibeProducts: configuredVibeProducts.length ? configuredVibeProducts : fallback.vibeProducts,
    };
  }

  const rankingBlock = runtimeConfig.marketing_data?.ranking_block;
  const sceneBlock = runtimeConfig.marketing_data?.scene_products;
  const configuredRankings =
    rankingBlock?.visible === false
      ? []
      : (rankingBlock?.rankings || []).filter((item) => item.visible !== false).slice(0, 3);
  const configuredScenes =
    sceneBlock?.visible === false
      ? []
      : (sceneBlock?.scenes || []).filter((item) => item.visible !== false);

  if (!configuredRankings.length && !configuredScenes.length) return fallback;

  const rankings: BestsellerPageData['rankings'] = {};
  const tabs = configuredRankings.flatMap((item, index) => {
    const rankingProducts = moduleProducts(item, rankingProductsByIndex);
    if (!rankingProducts.length) return [];

    const label =
      item.tab_title || item.title || fallback.tabs[index]?.label || `Ranking ${index + 1}`;
    const id = tabId(label, index);
    rankings[id] = rankingProducts.map((product, productIndex) =>
      mapRuntimeBestsellerProduct(
        product,
        productIndex + 1,
        s3BaseUrl,
        id === 'trending' ? 'trend' : 'sales',
        index < 2,
      ),
    );
    return [
      {
        id,
        label,
        eyebrow: item.sub_title || rankingBlock?.sub_title || '',
        showProductLabel: index < 2,
      },
    ];
  });

  const vibes: BestsellerVibe[] = configuredScenes.map((item, index) => {
    const label = item.title || fallback.vibes[index]?.label || `Vibe ${index + 1}`;
    const sceneProducts = moduleProducts(item, sceneProductsByIndex);
    return {
      id: sceneId(label, index),
      label,
      products: sceneProducts.map((product, productIndex) =>
        mapRuntimeBestsellerProduct(product, productIndex + 1, s3BaseUrl),
      ),
      total: sceneProductTotalsByIndex.get(item.item_index ?? index) ?? sceneProducts.length,
    };
  });
  const configuredVibeProducts = vibes.find((vibe) => vibe.products?.length)?.products || [];

  return {
    hero: {
      title: runtimeConfig.seo?.hOne || fallback.hero.title,
      subtitle: runtimeConfig.ad_banner?.sub_title || '',
      trackingJumpContent: runtimeConfig.ad_banner?.jump_content,
    },
    tabs,
    rankingNote: rankingBlock?.sub_title || '',
    rankings,
    vibeHeading: sceneBlock?.title || fallback.vibeHeading,
    vibeSubtitle: sceneBlock?.sub_title || fallback.vibeSubtitle,
    vibes: vibes.length ? vibes : fallback.vibes,
    vibeProducts: configuredVibeProducts.length ? configuredVibeProducts : fallback.vibeProducts,
  };
}
